import { DatePicker, Form, Switch } from 'antd';
import CalendarIcon from '../../icons/CalendarBlank.svg?react';
import CaretDownIcon from '../../icons/CaretDown.svg?react';
import MinusIcon from '../../icons/Minus.svg?react';
import moment, { Moment } from 'moment';
import { useState } from 'react';
import style from './style.module.scss';
import { RangeValue } from 'rc-picker/lib/interface';
import {
    handleDatePickerChange,
    isOutsideMaxPeriod,
} from '~utils/date-picker-helpers';
import { ProvinceCodesSelectTree } from '~components/region-selectors/province-codes-select-tree';
import type { ApiPeriod } from '~containers/analytics/kpis/periods';
import { PeriodPresets } from './period-presets';
import {
    CampaignWindow,
    isCampaignShortened,
} from './period-presets/period-presets.helpers';

const { RangePicker } = DatePicker;

interface Props {
    startDate: string | undefined;
    endDate: string | undefined;
    updateParams: (dates: RangeValue<Moment>) => void;
    onCompareChange?: (checked: boolean) => void; // If provided, show checkbox and invoke on checkbox change
    /**
     * Controls the compare Switch's checked state (only meaningful alongside
     * `onCompareChange`). Optional and defaults to leaving the Switch
     * uncontrolled (unchecked), so existing callers that only pass
     * `onCompareChange` keep their current behaviour unchanged.
     */
    compareEnabled?: boolean;
    regionFilter?: {
        label?: string;
        value?: string[];
        onChange?: (value: string[]) => void;
        disabled?: boolean;
    };
    /**
     * Opt in to the period shortcuts, shown under the date picker. `campaign`
     * is itself optional inside it: a dashboard that is not a campaign gets
     * the trailing windows only. `hasToDateTotals` opts into the shortened
     * `Campaign` label, which speaks of to-date totals.
     */
    periodPresets?: {
        active: ApiPeriod | undefined;
        onSelect: (preset: ApiPeriod) => void;
        campaign?: CampaignWindow;
        hasToDateTotals?: boolean;
    };
    /** The last day the picker offers; later days are greyed out. */
    lastSelectableDay?: string;
    /**
     * Show the date picker's clear (x). Off by default: it covers the calendar
     * icon on hover, and on most pages a shortcut or a new range replaces the
     * period anyway. A page where no period is a choice of its own, such as
     * Feedbacks showing every feedback, turns it on, since nothing else can
     * get back to no period.
     */
    allowClear?: boolean;
}

/**
 * The analytics filter row, as in the Road to Rewards Figma and the same on
 * every page that uses it: Period, with its shortcuts under the date picker
 * when the page has any, then Region beside it, the compare toggle at the far
 * right, and a divider under it all.
 */
export const AnalyticsSearchOptions = (props: Props) => {
    // The bound already placed in the open panel, which is what the period cap
    // is measured from. Cleared whenever the panel opens so a range selected
    // earlier never constrains the next one.
    const [picking, setPicking] = useState<RangeValue<Moment>>(null);

    const rangePicker = (
        <RangePicker
            size="large"
            allowClear={props.allowClear ?? false}
            allowEmpty={[true, true]}
            defaultValue={[
                props.startDate ? moment(props.startDate) : null,
                props.endDate ? moment(props.endDate) : null,
            ]}
            value={[
                props.startDate ? moment(props.startDate) : null,
                props.endDate ? moment(props.endDate) : null,
            ]}
            onChange={(dates: RangeValue<moment.Moment>) => {
                handleDatePickerChange(dates, props.updateParams, () =>
                    props.updateParams(null)
                );
            }}
            disabledDate={(current) =>
                isOutsideMaxPeriod(current, picking) ||
                (props.lastSelectableDay !== undefined &&
                    current.format('YYYY-MM-DD') > props.lastSelectableDay)
            }
            onCalendarChange={setPicking}
            onOpenChange={(open) => setPicking(open ? [null, null] : null)}
            format="YYYY-MM-DD"
            // The CMS's own icons (src/icons), as in the Figma.
            separator={<MinusIcon className={style.range_separator} />}
            suffixIcon={<CalendarIcon className={style.range_icon} />}
        />
    );

    const regionItem = props.regionFilter && (
        <Form.Item
            label={props.regionFilter.label ?? 'Region'}
            colon={false}
            className={style.form_item}
        >
            <ProvinceCodesSelectTree
                value={props.regionFilter.value}
                disabled={props.regionFilter.disabled}
                onChange={props.regionFilter.onChange}
                summarizeAll
                showArrow
                suffixIcon={<CaretDownIcon className={style.caret_icon} />}
            />
        </Form.Item>
    );

    // Rendered only when `onCompareChange` is provided.
    const compare = props.onCompareChange && (
        <Form className={style.compare_form}>
            <Form.Item
                label="Compare to previous period"
                name="compare"
                colon={false}
                className={style.form_item}
            >
                <Switch
                    checked={props.compareEnabled}
                    onChange={(checked) => props.onCompareChange?.(checked)}
                />
            </Form.Item>
        </Form>
    );

    // The shortcuts sit under the date picker inside the Period item, so
    // they read as part of the period rather than as a third filter, and
    // Region stays level with Period.
    return (
        <div className={style.filter_row}>
            <Form layout="inline" className={style.filter_row_form}>
                <Form.Item
                    label="Period"
                    colon={false}
                    className={style.form_item}
                >
                    <div className={style.period_controls}>
                        {rangePicker}
                        {props.periodPresets && (
                            <PeriodPresets
                                active={props.periodPresets.active}
                                onSelect={props.periodPresets.onSelect}
                                hasCampaign={!!props.periodPresets.campaign}
                                campaignShortened={
                                    !!props.periodPresets.hasToDateTotals &&
                                    isCampaignShortened(
                                        props.periodPresets.campaign
                                    )
                                }
                                appearance="segmented"
                            />
                        )}
                    </div>
                </Form.Item>
                {regionItem}
            </Form>
            {compare}
        </div>
    );
};
